/**
 * A-GTCC-VIEW-ALIN1 — "Customer Launch" (launch), 1:1 after
 * `docs/design/GTCC_REFERENCE_2026-09-01.html:900-917`.
 *
 * Element for element of the reference:
 *   - head title "Customer Launch" + freshness badge, `.sub` line,
 *   - `tiles`: READY (ok) / NEEDS ACTION (warn) / BLOCKED (crit) /
 *     MANUAL CHECK (info) with `big` numbers,
 *   - panel "Tenants nach Startbereitschaft" with TENANT / TYP · STATUS /
 *     BESTAND / MAIL / STATUS / "Checkliste → 360°",
 *   - the `.sub` sentence under the table, word for word.
 *
 * No new endpoint: `GET /v1/admin/customer-launch-overview` already carries
 * every column (see the measurement recorded in `../../api/launch.ts`). The
 * four tile numbers are counted from the rows that endpoint sent — a
 * measurement, not an invention — and a `capped` response says so instead of
 * presenting a partial count as complete.
 *
 * Deviations from the reference, both forced by the contract rather than
 * chosen here:
 *   - the `.sub` line drops "Beispieldaten." (real endpoint),
 *   - the freshness badge shows the render-independent `measuredAt` the
 *     endpoint sends only if it sends one; this endpoint carries no GTCC
 *     freshness envelope yet, so the badge is omitted rather than filled with
 *     a locally computed age (Owner honesty rules P8/P9/P10).
 */
import { EmptyState, ErrorState, LoadingState, Stack } from "@genius-tracker/design-system";
import { activeLocale, type Locale } from "@genius-tracker/i18n";
import { useLocale } from "@genius-tracker/i18n/react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useCallback, type CSSProperties, type ReactNode } from "react";

import {
  LAUNCH_OVERVIEW_KEY,
  LAUNCH_STATUSES,
  fetchLaunchOverview,
  sortLaunchRows,
  summariseLaunchRows,
  type LaunchOverviewDto,
  type LaunchResult,
  type LaunchRowDto,
  type LaunchStatus,
} from "../../api/launch.js";
import { gtccPathForResolvedViewKey } from "../../deep-link-url.js";
import { Dot, type CockpitTone } from "../../ui/primitives.js";
/* Die Kachel traegt ihren Ton als eingefaerbte linke Kante wie `ControlTile`
   (Abnahme E-GTCC-ACCEPT1); Rahmen und Tonfarbe kommen aus den geteilten
   Konstanten, damit die Anatomie an einer Stelle steht. Die `.st`-Statuszeile
   zeichnet die Referenz hier nicht. */
import {
  ControlPanel,
  TABLE_STYLE,
  TD_STYLE,
  TH_STYLE,
  TILE_BIG_STYLE,
  TILE_FRAME_STYLE,
  TILE_LABEL_STYLE,
  toneColor,
} from "../control/ControlPrimitives.js";

/** Whole numbers — host counts, vehicles, devices — in the locale's digits. */
export function formatInteger(value: number, locale: Locale = activeLocale()): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value);
}

/**
 * Same recipe as `GtccShell.tsx:137`. A GTCC table keeps the reference's
 * silent header cells (the dot column, the link column), and the repo's table
 * inventory demands that every column header carries a name and every table a
 * caption — so the name is rendered and hidden rather than left out.
 */
const GTCC_VISUALLY_HIDDEN: CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
};

/** The em dash the reference uses where a cell is simply empty. */
function FleetDash(props: { testId?: string }): ReactNode {
  return (
    <span data-testid={props.testId} style={{ color: "var(--gt-sys-text-muted)" }}>
      &mdash;
    </span>
  );
}

/**
 * `Dot` (`ui/primitives.tsx`) carries no `testId`/`data-tone` props of its
 * own; the wrapper below is the call-site adaptation that keeps those two
 * attributes (asserted by `LaunchView.test.tsx`) without bending the shared
 * primitive.
 */
function LaunchDot(props: { tone: CockpitTone; testId: string }): ReactNode {
  return (
    <span data-testid={props.testId} data-tone={props.tone}>
      <Dot tone={props.tone} />
    </span>
  );
}

/* --------------------------------------------------------------- queries */

const LAUNCH_STALE_MS = 30_000;
const LAUNCH_REFETCH_MS = 60_000;

const LAUNCH_QUERY_OPTIONS = {
  staleTime: LAUNCH_STALE_MS,
  refetchInterval: LAUNCH_REFETCH_MS,
  refetchOnWindowFocus: false,
} as const;

/* ----------------------------------------------------------------- tones */

/** Tile and dot colour of the reference, one tone per verdict. */
export const LAUNCH_TONE: Readonly<Record<LaunchStatus, CockpitTone>> = {
  ready: "ok",
  needs_action: "warn",
  blocked: "crit",
  manual: "info",
};

/**
 * The target of "Checkliste → 360°".
 *
 * The checklist lives in the Tenant-360° view. `ten` (Welle 2) is not merged,
 * so this links to the registry route that exists today; when `ten` lands the
 * key stays the same and only the registry entry behind it changes.
 *
 * That the key really is a registry route is pinned by `LaunchView.test.tsx`
 * rather than checked here: checking it at render time would mean importing
 * `deep-link.js`, and that closes the cycle Registry -> View -> deep-link ->
 * Registry, which silently leaves this view's registry entry without a
 * component (see `../../deep-link-url.ts`).
 */
export const LAUNCH_CHECKLIST_VIEW_KEY = "ten";

/* ----------------------------------------------------------------- styles */

const HEAD_ROW_STYLE: CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: "var(--gt-sys-space-3)",
  flexWrap: "wrap",
};

/*
 * Head style of the reference `h1`. The element itself is an `h2`: inside the
 * cockpit the `h1` belongs to `GtccShell` (`gtcc-shell-heading`), and a second
 * one would give the page two document titles. Every GTCC view therefore opens
 * at level 2 (see `views/risk/GtccRiskView.tsx:113`); the drawing is unchanged.
 */
/*
 * C-GTCC-SHELL-1ZU1 — Seitentitel = `h1` der Referenz
 * (GTCC_REFERENCE_2026-09-01.html:67): Sora 19 px/700 in `--tx`. Die Werte
 * kommen aus dem Token-Satz, damit jede Deck-View denselben Titel traegt;
 * vorher hat jede View ihre eigene Typo-Stufe gesetzt (15/19/20/24 px).
 */
const HEAD_TITLE_STYLE: CSSProperties = {
  margin: 0,
  fontFamily: "var(--gt-cmp-gtcc-view-title-family)",
  fontSize: "var(--gt-cmp-gtcc-view-title-size)",
  fontWeight: "var(--gt-cmp-gtcc-view-title-weight)",
  color: "var(--tx)",
};

const SUB_STYLE: CSSProperties = {
  margin: 0,
  fontSize: "var(--gt-sys-font-size-sm)",
  color: "var(--gt-sys-text-muted)",
};

const TILES_STYLE: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: "var(--gt-sys-space-3)",
};

/*
 * Etikett und `.big` kommen jetzt aus `ControlPrimitives` (Abnahme
 * A-GTCC-ACCEPT-ALL2, Befund „Kachelhoehe"): die lokale Kopie setzte die Zahl
 * auf `font-size-3xl` (Kastenhoehe 35 px gemessen), waehrend `.big` der
 * Referenz 23 px ist (25 px) und die kanonische Konstante dafuer `2xl` haelt.
 * Die Kachel war dadurch hoeher als die Referenz und hat die Tabelle nach
 * unten geschoben. Kein zweiter Wert hier — der Satz steht einmal, dort.
 */
/*
 * A-GTCC-ROWGRID1: Schrift, Kopf- und Zellenmasse kommen aus den kanonischen
 * `ControlPrimitives` (Ursache 2 des Abgleichs A-GTCC-ACCEPT-ALL2). Lokal
 * bleibt nur die Trennlinie, die `th` und `td` der Referenz tragen
 * (`GTCC_REFERENCE_2026-09-01.html`: `th{… border-bottom:1px solid var(--line)}`)
 * und die die geteilten Konstanten nicht setzen. Die fehlende Unterkante in
 * `TD_STYLE`/`TH_STYLE` ist als Rest notiert, nicht hier zentral geaendert.
 */
const BOTTOM_RULE: CSSProperties = {
  borderBlockEndWidth: 1,
  borderBlockEndStyle: "solid",
  borderBlockEndColor: "var(--gt-sys-border)",
};

const HEAD_CELL_STYLE: CSSProperties = { ...TH_STYLE, ...BOTTOM_RULE };

const CELL_STYLE: CSSProperties = { ...TD_STYLE, ...BOTTOM_RULE };

const MONO_CELL_STYLE: CSSProperties = {
  ...CELL_STYLE,
  fontFamily: "var(--gt-sys-font-family-mono)",
  fontSize: "var(--gt-sys-font-size-xs)",
  whiteSpace: "nowrap",
};

const HOST_CELL_STYLE: CSSProperties = {
  ...CELL_STYLE,
  fontWeight: "var(--gt-sys-font-weight-semibold)",
};

const LINK_CELL_STYLE: CSSProperties = {
  ...MONO_CELL_STYLE,
  color: "var(--gt-sys-accent)",
};

function LaunchGate<T>(props: {
  query: UseQueryResult<LaunchResult<T>>;
  testId: string;
  render: (data: T) => ReactNode;
}): ReactNode {
  const { t } = useLocale();
  const { query } = props;

  /*
   * `fetchLaunchOverview` turns every failure into a `LaunchResult`, so a
   * rejected query is the one case it cannot describe: the request never got
   * far enough to answer. It is still a failure and is shown as one — treating
   * it as "still loading" would leave a spinner spinning forever.
   */
  if (query.data !== undefined && query.data.ok) return props.render(query.data.data);

  const failure =
    query.data !== undefined
      ? query.data
      : query.isError
        ? ({ ok: false, kind: "transport_error", message: String(query.error) } as const)
        : null;

  if (failure === null) {
    return (
      <div data-testid={`${props.testId}-loading`}>
        <LoadingState label={t("admin.gtcc.launch.view.loading")} />
      </div>
    );
  }

  const body =
    failure.kind === "disabled"
      ? t("admin.gtcc.launch.view.failure.disabled")
      : failure.kind === "forbidden"
        ? t("admin.gtcc.launch.view.failure.forbidden")
        : failure.kind === "upstream"
          ? t("admin.gtcc.launch.view.failure.upstream", { status: failure.httpStatus })
          : t("admin.gtcc.launch.view.failure.transport");
  return (
    <div data-testid={`${props.testId}-failure`}>
      <ErrorState severity="warn" title={t("admin.gtcc.launch.view.failure.title")} body={body} />
    </div>
  );
}

/* ------------------------------------------------------------------ tiles */

function LaunchTiles(props: { rows: ReadonlyArray<LaunchRowDto> }): ReactNode {
  const { t, locale } = useLocale();
  const counts = summariseLaunchRows(props.rows);

  return (
    <div style={TILES_STYLE} data-testid="gtcc-launch-tiles">
      {LAUNCH_STATUSES.map((status) => (
        <div
          key={status}
          data-testid={`gtcc-launch-tile-${status}-frame`}
          data-tone={LAUNCH_TONE[status]}
          style={{
            ...TILE_FRAME_STYLE,
            borderInlineStartColor: toneColor(LAUNCH_TONE[status]),
          }}
        >
          {/* Ohne `Stack`: `TILE_FRAME_STYLE` ist selbst schon die Spalte mit
              Abstand `space-1`, wie `ControlTile` sie zeichnet. Der zusaetzliche
              Rahmen hat nur eine zweite Flexbox in die Kachel gesetzt. */}
          <h3 style={TILE_LABEL_STYLE} data-testid={`gtcc-launch-tile-${status}-label`}>
            {t(`admin.gtcc.launch.view.tile.${status}`)}
          </h3>
          {/* Die Referenz traegt die Farbe der Kachel allein im linken Rand
              (`<div class="big">0</div>`,
              `GTCC_REFERENCE_2026-09-01.html:903-906`); ein zweiter Punkt vor
              der Zahl steht dort nicht (Abnahme E-GTCC-ACCEPT2). Die Lesbarkeit
              ohne Farbe traegt weiterhin die Kachel-Ueberschrift READY /
              NEEDS ACTION / BLOCKED / MANUAL CHECK. */}
          <span style={TILE_BIG_STYLE} data-testid={`gtcc-launch-tile-${status}`}>
            {formatInteger(counts[status], locale)}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ cells */

/** `BESTAND` of the reference: "1 Admin · 155 Fzg. · 155 Geräte". */
export function formatInventory(
  row: LaunchRowDto,
  locale: Locale,
  t: (key: string, params?: Record<string, string | number>) => string,
): string {
  /*
   * Reference shape: "1 Admin · 155 Fzg. · 155 Geräte" (URBAGEC) but
   * "1 Admin · 36 Fzg." (SMDTT) — a zero fleet or device count drops its
   * segment instead of printing "0". The admin segment always stays: "0 Admin"
   * is exactly the sentence that explains a NEEDS ACTION verdict, so hiding it
   * would hide the reason.
   */
  const segments = [
    t("admin.gtcc.launch.view.row.admins", {
      count: formatInteger(row.activeAdminCount, locale),
    }),
  ];
  if (row.vehicleCount > 0) {
    segments.push(
      t("admin.gtcc.launch.view.row.vehicles", {
        count: formatInteger(row.vehicleCount, locale),
      }),
    );
  }
  if (row.deviceCount > 0) {
    segments.push(
      t("admin.gtcc.launch.view.row.devices", {
        count: formatInteger(row.deviceCount, locale),
      }),
    );
  }
  return segments.join(" · ");
}

/* ------------------------------------------------------------------ table */

function LaunchTable(props: {
  data: LaunchOverviewDto;
  onOpenChecklist: (tenantId: string) => void;
  checklistHref: string;
}): ReactNode {
  const { t, locale } = useLocale();
  const rows = sortLaunchRows(props.data.items);

  if (rows.length === 0) {
    return (
      <div data-testid="gtcc-launch-table-empty">
        <EmptyState
          title={t("admin.gtcc.launch.view.empty.title")}
          body={t("admin.gtcc.launch.view.empty.body")}
        />
      </div>
    );
  }

  return (
    <Stack gap={1}>
      <table style={TABLE_STYLE} data-testid="gtcc-launch-table">
        {/* The reference prints no caption and leaves the link column headless.
            Both names are rendered and visually hidden instead of dropped: the
            repo's table inventory requires a named table and a named column
            header, and the drawing stays byte-identical. */}
        <caption style={GTCC_VISUALLY_HIDDEN}>{t("admin.gtcc.launch.view.table.title")}</caption>
        <thead>
          <tr>
            <th scope="col" style={HEAD_CELL_STYLE}>
              {t("admin.gtcc.launch.view.col.tenant")}
            </th>
            <th scope="col" style={HEAD_CELL_STYLE}>
              {t("admin.gtcc.launch.view.col.type")}
            </th>
            <th scope="col" style={HEAD_CELL_STYLE}>
              {t("admin.gtcc.launch.view.col.inventory")}
            </th>
            <th scope="col" style={HEAD_CELL_STYLE}>
              {t("admin.gtcc.launch.view.col.mail")}
            </th>
            <th scope="col" style={HEAD_CELL_STYLE}>
              {t("admin.gtcc.launch.view.col.status")}
            </th>
            <th scope="col" style={HEAD_CELL_STYLE}>
              <span style={GTCC_VISUALLY_HIDDEN}>{t("admin.gtcc.launch.view.col.checklist")}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.tenantId} data-testid={`gtcc-launch-row-${row.tenantId}`}>
              <td style={HOST_CELL_STYLE}>{row.displayName ?? row.tenantId}</td>
              <td style={MONO_CELL_STYLE}>{`${row.type} · ${row.subscriptionStatus}`}</td>
              <td style={MONO_CELL_STYLE}>{formatInventory(row, locale, t)}</td>
              <td style={MONO_CELL_STYLE} data-testid={`gtcc-launch-mail-${row.tenantId}`}>
                {row.latestEmailDeliveryStatus === null ? (
                  <FleetDash testId={`gtcc-launch-mail-dash-${row.tenantId}`} />
                ) : (
                  row.latestEmailDeliveryStatus
                )}
              </td>
              <td style={CELL_STYLE}>
                <span style={{ display: "inline-flex", alignItems: "center" }}>
                  <LaunchDot
                    tone={LAUNCH_TONE[row.overall]}
                    testId={`gtcc-launch-status-dot-${row.tenantId}`}
                  />
                  <span data-testid={`gtcc-launch-status-${row.tenantId}`}>
                    {t(`admin.gtcc.launch.view.status.${row.overall}`)}
                  </span>
                </span>
              </td>
              <td style={LINK_CELL_STYLE}>
                <a
                  href={props.checklistHref}
                  style={{ color: "var(--gt-sys-accent)" }}
                  onClick={(event) => {
                    event.preventDefault();
                    props.onOpenChecklist(row.tenantId);
                  }}
                  data-testid={`gtcc-launch-checklist-${row.tenantId}`}
                >
                  {t("admin.gtcc.launch.view.checklist_link")}
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p style={SUB_STYLE} data-testid="gtcc-launch-table-note">
        {t("admin.gtcc.launch.view.table_note")}
      </p>
      {props.data.capped ? (
        <p style={SUB_STYLE} data-testid="gtcc-launch-capped">
          {t("admin.gtcc.launch.view.capped", {
            shown: formatInteger(rows.length, locale),
            total: formatInteger(props.data.total, locale),
          })}
        </p>
      ) : null}
    </Stack>
  );
}

/* ------------------------------------------------------------------ view */

export function LaunchView(): ReactNode {
  const { t } = useLocale();

  const query = useQuery({
    queryKey: LAUNCH_OVERVIEW_KEY,
    queryFn: ({ signal }) => fetchLaunchOverview(signal),
    ...LAUNCH_QUERY_OPTIONS,
  });

  const checklistHref =
    typeof window === "undefined"
      ? "#"
      : gtccPathForResolvedViewKey(
          window.location,
          LAUNCH_CHECKLIST_VIEW_KEY,
          import.meta.env.BASE_URL,
        );

  /*
   * Cross-view navigation without reaching into the shell: push the canonical
   * path and dispatch `popstate`, which `GtccShell` already listens to. The
   * tenant travels as `?tenant=`, the parameter the 360° view reads.
   */
  const openChecklist = useCallback(
    (tenantId: string) => {
      if (typeof window === "undefined") return;
      const url = new URL(checklistHref, window.location.origin);
      url.searchParams.set("tenant", tenantId);
      window.history.pushState(null, "", `${url.pathname}${url.search}${url.hash}`);
      window.dispatchEvent(new PopStateEvent("popstate"));
    },
    [checklistHref],
  );

  return (
    <Stack gap={3} data-testid="gtcc-launch">
      {/* Kein Abstandsrahmen zwischen Titel und `.sub`: die Referenz setzt die
          Unterzeile ohne Zwischenraum unter den Titel, genau wie `ControlHead`
          es zeichnet. Der `Stack gap={1}` hat hier 4 px eingefuegt, die die
          Referenz nicht hat (Abnahme A-GTCC-ACCEPT-ALL2). */}
      <div>
        <div style={HEAD_ROW_STYLE}>
          <h2 style={HEAD_TITLE_STYLE} data-testid="gtcc-launch-title">
            {t("admin.gtcc.launch.view.title")}
          </h2>
        </div>
        <p style={SUB_STYLE} data-testid="gtcc-launch-sub">
          {t("admin.gtcc.launch.view.subtitle")}
        </p>
      </div>

      <LaunchGate
        query={query}
        testId="gtcc-launch-tiles-gate"
        render={(data: LaunchOverviewDto) => <LaunchTiles rows={data.items} />}
      />

      {/* `ControlPanel` statt einer zweiten, hier gebauten Tafel: gleiche
          Anatomie (flache Karte, `h3`, Abstand `space-2`), aber eine Stelle
          (AGENTS.md, Shared UI Foundation Rule). */}
      <ControlPanel title={t("admin.gtcc.launch.view.table.title")} testId="gtcc-launch-panel">
        <LaunchGate
          query={query}
          testId="gtcc-launch-panel"
          render={(data: LaunchOverviewDto) => (
            <LaunchTable
              data={data}
              checklistHref={checklistHref}
              onOpenChecklist={openChecklist}
            />
          )}
        />
      </ControlPanel>
    </Stack>
  );
}
